import {
  AudioWaveform,
  BarChart3,
  ChartSpline,
  Droplets,
  Gauge,
  Globe2,
  Layers3,
  Mountain,
  Radiation,
  Scale,
  ShieldPlus,
  ScanSearch,
  Sprout,
  Sun,
  Target,
  Telescope,
  Trees,
  Waves,
} from 'lucide-react';

import { earthSciencePreviewEnabled } from '../../../lib/earthScience';

import { EARTH_SCIENCE_TOOLS_ENABLED } from './earthScienceTools';
import type { StudyTool } from './studyToolTypes';

/** Earth and space Science tools, listed only behind their preview flags (split out of studyTools.ts, SIR-HYG-07). */
const EARTH_SCIENCE_ENABLED = earthSciencePreviewEnabled();

export const EARTH_ENERGY_BUDGET_TOOLS: StudyTool[] = EARTH_SCIENCE_ENABLED
  ? [{
      id: 'earth-energy-budget',
      subjectKey: 'science',
      category: 'earth-space',
      name: 'Earth Energy Budget Lab',
      description: 'Predict how reflection changes absorbed energy, then test the model and explain its limits.',
      path: '/learn/study/subjects/science/tools/earth-energy-budget',
      actionLabel: 'Open energy budget',
      icon: Globe2,
      tone: 'sky',
    }]
  : [];

export const EARTH_SCIENCE_SUITE_TOOLS: StudyTool[] = EARTH_SCIENCE_TOOLS_ENABLED
  ? [
      {
        id: 'solar-system-seasons',
        subjectKey: 'science',
        category: 'earth-space',
        name: 'Solar System & Seasons Model',
        description: 'Move Earth through the year and test how tilt and latitude change daylight.',
        path: '/learn/study/subjects/science/tools/solar-system-seasons',
        actionLabel: 'Open seasons model',
        icon: Sun,
        tone: 'sunshine',
        availableFrom: 6,
        yearLabel: 'Year 6 candidate',
      },
      {
        id: 'earth-moon-sun-cycles',
        subjectKey: 'science',
        category: 'earth-space',
        name: 'Earth–Sun–Moon Cycles Lab',
        description: 'Move the Moon through a month and test why phase alone does not make an eclipse.',
        path: '/learn/study/subjects/science/tools/earth-moon-sun-cycles',
        actionLabel: 'Open Earth–Sun–Moon lab',
        icon: Telescope,
        tone: 'sky',
        availableFrom: 7,
        yearLabel: 'Year 7 candidate',
      },
      {
        id: 'plate-tectonics-hazards',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Plate Tectonics & Hazards Lab',
        description: 'Compare boundary types, movement over time, landforms and hazard patterns.',
        path: '/learn/study/subjects/science/tools/plate-tectonics-hazards',
        actionLabel: 'Open plate tectonics lab',
        icon: Mountain,
        tone: 'coral',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'rock-cycle-minerals',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Rock Cycle & Minerals Explorer',
        description: 'Trace rock pathways and compare hardness, streak, acid reaction and cleavage evidence.',
        path: '/learn/study/subjects/science/tools/rock-cycle-minerals',
        actionLabel: 'Open rock and minerals explorer',
        icon: Layers3,
        tone: 'mint',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'carbon-cycle-systems',
        subjectKey: 'science',
        category: 'earth-systems',
        name: 'Carbon Cycle & Earth Systems Lab',
        description: 'Balance photosynthesis, respiration and combustion without confusing flows and reservoirs.',
        path: '/learn/study/subjects/science/tools/carbon-cycle-systems',
        actionLabel: 'Open carbon cycle lab',
        icon: Trees,
        tone: 'mint',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'weather-systems-evidence',
        subjectKey: 'science',
        category: 'weather-climate',
        name: 'Weather Systems & Forecast Evidence Lab',
        description: 'Combine temperature, humidity and pressure evidence without claiming a certain forecast.',
        path: '/learn/study/subjects/science/tools/weather-systems-evidence',
        actionLabel: 'Open weather evidence lab',
        icon: Gauge,
        tone: 'sky',
        availableFrom: 7,
        yearLabel: 'Year 7 candidate',
      },
      {
        id: 'water-catchments-groundwater',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Water, Catchments & Groundwater Lab',
        description: 'Partition rainfall into runoff, infiltration and a groundwater recharge proxy.',
        path: '/learn/study/subjects/science/tools/water-catchments-groundwater',
        actionLabel: 'Open catchment lab',
        icon: Droplets,
        tone: 'sky',
        availableFrom: 7,
        yearLabel: 'Year 7 candidate',
      },
      {
        id: 'geological-time-fossils',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Geological Time, Fossils & Strata Lab',
        description: 'Test superposition with layer ages and fossil evidence, including conflicting sequences.',
        path: '/learn/study/subjects/science/tools/geological-time-fossils',
        actionLabel: 'Open geological time lab',
        icon: ScanSearch,
        tone: 'sunshine',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'natural-hazards-risk',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Natural Hazards Risk & Preparedness Lab',
        description: 'Compare hazard, exposure, vulnerability and preparedness without predicting an event.',
        path: '/learn/study/subjects/science/tools/natural-hazards-risk',
        actionLabel: 'Open natural hazards lab',
        icon: ShieldPlus,
        tone: 'coral',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'ocean-circulation-climate',
        subjectKey: 'science',
        category: 'earth-systems',
        name: 'Ocean Circulation & Climate Lab',
        description: 'Change seawater temperature and salinity to compare density and vertical tendency.',
        path: '/learn/study/subjects/science/tools/ocean-circulation-climate',
        actionLabel: 'Open ocean circulation lab',
        icon: Waves,
        tone: 'mint',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'universe-redshift-scale',
        subjectKey: 'science',
        category: 'earth-space',
        name: 'Redshift & Expanding Universe Evidence Lab',
        description: 'Compare a spectral line at rest and in an observed source, then calculate redshift.',
        path: '/learn/study/subjects/science/tools/universe-redshift-scale',
        actionLabel: 'Open redshift evidence lab',
        icon: Telescope,
        tone: 'sky',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'topographic-maps-slope',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Topographic Map & Slope Lab',
        description: 'Use contour elevations and horizontal distance to calculate relief and average slope.',
        path: '/learn/study/subjects/science/tools/topographic-maps-slope',
        actionLabel: 'Open topographic slope lab',
        icon: Mountain,
        tone: 'mint',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'geological-cross-sections',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Geological Cross-section Projection Lab',
        description: 'Project a constant-dip layer and identify the evidence needed to test the section.',
        path: '/learn/study/subjects/science/tools/geological-cross-sections',
        actionLabel: 'Open geological cross-section lab',
        icon: Layers3,
        tone: 'sunshine',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'earth-observation-vegetation',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Earth Observation & Vegetation Signal Lab',
        description: 'Calculate NDVI and decide when cloud makes a satellite pixel unreliable.',
        path: '/learn/study/subjects/science/tools/earth-observation-vegetation',
        actionLabel: 'Open Earth observation lab',
        icon: ScanSearch,
        tone: 'mint',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'field-transect-evidence',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Field Transect Evidence Lab',
        description: 'Plot three vegetation-cover samples and separate pattern from causal claims.',
        path: '/learn/study/subjects/science/tools/field-transect-evidence',
        actionLabel: 'Open field transect lab',
        icon: BarChart3,
        tone: 'coral',
        availableFrom: 7,
        yearLabel: 'Year 7 candidate',
      },
      {
        id: 'mineral-resources-recovery',
        subjectKey: 'science',
        category: 'resources-sustainability',
        name: 'Mineral Grade & Recovery Lab',
        description: 'Separate ore mass, contained commodity and process recovery without claiming a reserve.',
        path: '/learn/study/subjects/science/tools/mineral-resources-recovery',
        actionLabel: 'Open mineral recovery lab',
        icon: Mountain,
        tone: 'sunshine',
        availableFrom: 11,
        yearLabel: 'Year 11 candidate',
      },
      {
        id: 'atmosphere-lapse-stability',
        subjectKey: 'science',
        category: 'weather-climate',
        name: 'Atmospheric Lapse Rate & Stability Lab',
        description: 'Calculate a vertical temperature lapse rate and compare a dry-air stability pattern.',
        path: '/learn/study/subjects/science/tools/atmosphere-lapse-stability',
        actionLabel: 'Open atmospheric stability lab',
        icon: Gauge,
        tone: 'sky',
        availableFrom: 11,
        yearLabel: 'Year 11 candidate',
      },
      {
        id: 'biodiversity-quadrat-sampling',
        subjectKey: 'science',
        category: 'earth-systems',
        name: 'Biodiversity Quadrat Evidence Lab',
        description: 'Compare richness and abundance with a Simpson diversity calculation.',
        path: '/learn/study/subjects/science/tools/biodiversity-quadrat-sampling',
        actionLabel: 'Open biodiversity quadrat lab',
        icon: Sprout,
        tone: 'mint',
        availableFrom: 11,
        yearLabel: 'Year 11 candidate',
      },
      {
        id: 'resource-stock-sustainability',
        subjectKey: 'science',
        category: 'resources-sustainability',
        name: 'Renewable Resource Stock Lab',
        description: 'Project how regeneration and extraction change a renewable-resource stock.',
        path: '/learn/study/subjects/science/tools/resource-stock-sustainability',
        actionLabel: 'Open renewable resource lab',
        icon: Scale,
        tone: 'mint',
        availableFrom: 11,
        yearLabel: 'Year 11 candidate',
      },
      {
        id: 'environmental-time-series-uncertainty',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Environmental Change & Uncertainty Lab',
        description: 'Compare two observations and their uncertainties before claiming environmental change.',
        path: '/learn/study/subjects/science/tools/environmental-time-series-uncertainty',
        actionLabel: 'Open environmental uncertainty lab',
        icon: ChartSpline,
        tone: 'coral',
        availableFrom: 11,
        yearLabel: 'Year 11 candidate',
      },
      {
        id: 'seismic-wave-distance',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Seismic P–S Distance Lab',
        description: 'Pick P- and S-wave arrivals and distinguish station distance from epicentre direction.',
        path: '/learn/study/subjects/science/tools/seismic-wave-distance',
        actionLabel: 'Open seismic distance lab',
        icon: AudioWaveform,
        tone: 'coral',
        availableFrom: 8,
        yearLabel: 'Year 8 candidate',
      },
      {
        id: 'radiometric-dating-half-life',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Radiometric Dating & Half-life Lab',
        description: 'Translate remaining parent isotope into elapsed half-lives and a conditional age.',
        path: '/learn/study/subjects/science/tools/radiometric-dating-half-life',
        actionLabel: 'Open radiometric dating lab',
        icon: Radiation,
        tone: 'sunshine',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'storm-hydrograph-response',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Storm Runoff & Hydrograph Lab',
        description: 'Calculate runoff depth and volume while keeping peak lag as an entered scenario.',
        path: '/learn/study/subjects/science/tools/storm-hydrograph-response',
        actionLabel: 'Open storm hydrograph lab',
        icon: ChartSpline,
        tone: 'sky',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'soil-infiltration-erosion',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Soil Infiltration & Erosion Lab',
        description: 'Compare rainfall and infiltration, then test how cover and slope change erosion pressure.',
        path: '/learn/study/subjects/science/tools/soil-infiltration-erosion',
        actionLabel: 'Open soil erosion lab',
        icon: Sprout,
        tone: 'mint',
        availableFrom: 7,
        yearLabel: 'Year 7 candidate',
      },
      {
        id: 'groundwater-flow-gradient',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Groundwater Gradient & Flow Lab',
        description: 'Calculate hydraulic gradient, flow direction and Darcy flux between two monitoring points.',
        path: '/learn/study/subjects/science/tools/groundwater-flow-gradient',
        actionLabel: 'Open groundwater flow lab',
        icon: Droplets,
        tone: 'sky',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'earthquake-epicentre-triangulation',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Earthquake Epicentre Triangulation Lab',
        description: 'Fit one candidate source to distance evidence from three fixed seismic stations.',
        path: '/learn/study/subjects/science/tools/earthquake-epicentre-triangulation',
        actionLabel: 'Open epicentre triangulation lab',
        icon: Target,
        tone: 'coral',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'unconformity-missing-time',
        subjectKey: 'science',
        category: 'dynamic-earth',
        name: 'Unconformity & Missing Time Lab',
        description: 'Use dated units and uncertainty to constrain a gap in the rock record.',
        path: '/learn/study/subjects/science/tools/unconformity-missing-time',
        actionLabel: 'Open unconformity lab',
        icon: Layers3,
        tone: 'sunshine',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'catchment-water-balance',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Catchment Water Balance Lab',
        description: 'Balance precipitation, evapotranspiration, streamflow and storage residuals.',
        path: '/learn/study/subjects/science/tools/catchment-water-balance',
        actionLabel: 'Open catchment water balance lab',
        icon: Scale,
        tone: 'sky',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'climate-anomaly-baseline',
        subjectKey: 'science',
        category: 'weather-climate',
        name: 'Climate Anomaly & Baseline Lab',
        description: 'Calculate an anomaly against a defined baseline without claiming a trend from one point.',
        path: '/learn/study/subjects/science/tools/climate-anomaly-baseline',
        actionLabel: 'Open climate anomaly lab',
        icon: ChartSpline,
        tone: 'coral',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'satellite-vegetation-change',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Satellite Vegetation Change Lab',
        description: 'Compare before-and-after NDVI while keeping cloud and observation quality visible.',
        path: '/learn/study/subjects/science/tools/satellite-vegetation-change',
        actionLabel: 'Open satellite change lab',
        icon: ScanSearch,
        tone: 'mint',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'earthquake-catalogue-patterns',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Earthquake Catalogue Patterns Lab',
        description: 'Import catalogue rows and inspect magnitude, depth and spatial extent without predicting the next event.',
        path: '/learn/study/subjects/science/tools/earthquake-catalogue-patterns',
        actionLabel: 'Open earthquake catalogue lab',
        icon: Target,
        tone: 'coral',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'rainfall-station-timeseries',
        subjectKey: 'science',
        category: 'weather-climate',
        name: 'Rainfall Station Time-series Lab',
        description: 'Import one station series and compare its latest value with the imported-series mean.',
        path: '/learn/study/subjects/science/tools/rainfall-station-timeseries',
        actionLabel: 'Open rainfall series lab',
        icon: BarChart3,
        tone: 'sky',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
      {
        id: 'streamflow-station-hydrograph',
        subjectKey: 'science',
        category: 'water-systems',
        name: 'Streamflow Station Hydrograph Lab',
        description: 'Import discharge values and preserve validated, provisional and derived quality states.',
        path: '/learn/study/subjects/science/tools/streamflow-station-hydrograph',
        actionLabel: 'Open streamflow hydrograph lab',
        icon: Waves,
        tone: 'sky',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'fractional-cover-timeseries',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Fractional Cover Time-series Lab',
        description: 'Import green, brown and bare cover with observation-quality checks.',
        path: '/learn/study/subjects/science/tools/fractional-cover-timeseries',
        actionLabel: 'Open fractional cover lab',
        icon: ScanSearch,
        tone: 'mint',
        availableFrom: 10,
        yearLabel: 'Year 10 candidate',
      },
      {
        id: 'surface-geology-data-evidence',
        subjectKey: 'science',
        category: 'earth-data',
        name: 'Surface Geology Data Evidence Lab',
        description: 'Import mapped unit attributes and compare age ranges, lithology and map-scale limits.',
        path: '/learn/study/subjects/science/tools/surface-geology-data-evidence',
        actionLabel: 'Open surface geology data lab',
        icon: Layers3,
        tone: 'sunshine',
        availableFrom: 9,
        yearLabel: 'Year 9 candidate',
      },
    ]
  : [];
